import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, CheckCircle, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { LLMProvider } from '@/types';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import type { Translation } from '@/i18n/ru';
import { apiClient } from '@/utils/apiClient';
import { providerTestPassed } from '@/utils/providerTest';
import { isProviderFailing } from '@/utils/providerHealth';
import { toast } from '@/stores/toastStore';

/** STORY-520: the Failing badge's details (tooltip + screen-reader text). */
function failingDetails(provider: LLMProvider, t: Translation): string {
  const h = provider.health;
  const kind = h?.last_error_kind === 'timeout'
    ? t.settings.providerErrorKindTimeout
    : t.settings.providerErrorKindProviderError;
  const since = h?.failing_since
    ? new Date(h.failing_since * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';
  return t.settings.providerFailingTitle
    .replace('{kind}', kind)
    .replace('{count}', String(h?.consecutive_failures ?? 0))
    .replace('{since}', since);
}

interface LLMProviderSettingsProps {
  /** Called after a provider is activated or deactivated on the backend, so
   * embedding pages (the first-run wizard) can refresh their config-status. */
  onProvidersChanged?: () => void;
}

export const LLMProviderSettings: React.FC<LLMProviderSettingsProps> = ({ onProvidersChanged }) => {
  const {
    llmProviders,
    addLLMProvider,
    updateLLMProvider,
    deleteLLMProvider,
    setActiveLLMProvider,
    settings,
    updateSettings,
    triggerVaultRefresh,
    syncLLMProvidersFromBackend,
  } = useAppStore();
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [initializingProviders, setInitializingProviders] = useState<Set<string>>(new Set());
  const [providerErrors, setProviderErrors] = useState<Map<string, string>>(new Map());
  const [providerSuccess, setProviderSuccess] = useState<Set<string>>(new Set());

  // When this settings tab opens, pull providers from the backend so a device
  // that never added Ollama locally still sees it in the dropdown.
  useEffect(() => {
    syncLLMProvidersFromBackend();
  }, [syncLLMProvidersFromBackend]);

  const [formData, setFormData] = useState<{
    name: string;
    type: LLMProvider['type'];
    apiKey: string;
    endpoint: string;
    authUrl: string;
    certFile: string;
    keyFile: string;
    caBundleFile: string;
    model: string;
    settings: {
      maxTokens: number;
      temperature: number;
      topP: number;
      frequencyPenalty: number;
      presencePenalty: number;
    };
  }>({
    name: '',
    type: '' as LLMProvider['type'],
    apiKey: '',
    endpoint: '',
    authUrl: '',
    certFile: '',
    keyFile: '',
    caBundleFile: '',
    model: '',
    settings: {
      maxTokens: 128000,
      temperature: 0.7,
      topP: 1,
      frequencyPenalty: 0,
      presencePenalty: 0,
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: '' as LLMProvider['type'],
      apiKey: '',
      endpoint: '',
      authUrl: '',
      certFile: '',
      keyFile: '',
      caBundleFile: '',
      model: '',
      settings: {
        maxTokens: 128000,
        temperature: 0.7,
        topP: 1,
        frequencyPenalty: 0,
        presencePenalty: 0,
      },
    });
  };

  // Shared with handleActivateProvider's not-found fallback — builds the
  // backend /test and /add request body from either the form or a stored
  // LLMProvider.
  const buildProviderConfig = (
    data: {
      type: LLMProvider['type'];
      name: string;
      model: string;
      apiKey?: string;
      endpoint?: string;
      authUrl?: string;
      certFile?: string;
      keyFile?: string;
      caBundleFile?: string;
      settings: LLMProvider['settings'];
    },
    enabled: boolean
  ): any => {
    const providerConfig: any = {
      type: data.type,
      name: data.name,
      model: data.model,
      enabled,
      settings: { ...data.settings },
    };

    if (data.type === 'ollama') {
      providerConfig.endpoint = data.endpoint || 'http://localhost:11434';
    } else if (data.type === 'gigachat') {
      providerConfig.credentials = data.apiKey;
      providerConfig.scope = 'GIGACHAT_API_B2B';
      if (data.endpoint) providerConfig.endpoint = data.endpoint;
      if (data.authUrl) providerConfig.auth_url = data.authUrl;
      if (data.certFile) providerConfig.cert_file = data.certFile;
      if (data.keyFile) providerConfig.key_file = data.keyFile;
      if (data.caBundleFile) providerConfig.ca_bundle_file = data.caBundleFile;
    } else if (data.type === 'openai') {
      providerConfig.api_key = data.apiKey;
      providerConfig.endpoint = data.endpoint || 'https://api.openai.com/v1';
    } else if (data.type === 'anthropic') {
      providerConfig.api_key = data.apiKey;
      providerConfig.endpoint = data.endpoint || 'https://api.anthropic.com';
    } else if (data.apiKey) {
      providerConfig.api_key = data.apiKey;
      if (data.endpoint) providerConfig.endpoint = data.endpoint;
    }

    return providerConfig;
  };

  const [isRegistering, setIsRegistering] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingId) {
      // Editing an existing local entry stays local-only, same as before —
      // the backend registration (if any) is only touched by Activate/
      // Deactivate/Delete.
      updateLLMProvider(editingId, {
        ...formData,
        enabled: false,
      });
      setEditingId(null);
      setSecretsSet({});
      resetForm();
      return;
    }

    // Adding a brand-new provider now also registers it on the backend right
    // away — disabled — so credentials are actually saved and validated
    // before the user hits Activate.
    const providerConfig = buildProviderConfig(formData, false);
    setIsRegistering(true);
    try {
      const testResult = await apiClient.testLLMProvider(providerConfig);
      if (!providerTestPassed(testResult)) {
        throw new Error(testResult.message || t.settings.providerTestFailed);
      }
      const addResult = await apiClient.addLLMProvider(providerConfig);
      if (addResult.status !== 'success') {
        throw new Error(addResult.message || t.settings.providerInitializationFailed);
      }
      if (addResult.saved_to_storage === false) {
        toast.warning(t.settings.providerNotPersisted);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
      return; // keep the form open with the user's data so they can fix and retry
    } finally {
      setIsRegistering(false);
    }

    addLLMProvider({
      ...formData,
      enabled: false,
    });
    setIsAdding(false);
    setSecretsSet({});
    resetForm();
    toast.success(`${formData.name || formData.type} added`);
  };

  // Track which secret fields are already set on the backend
  const [secretsSet, setSecretsSet] = useState<Record<string, boolean>>({});

  const handleEdit = async (provider: LLMProvider) => {
    // Start with local store values as baseline
    setFormData({
      name: provider.name,
      type: provider.type,
      apiKey: '',  // never pre-fill secrets — show placeholder instead
      endpoint: provider.endpoint || '',
      authUrl: provider.authUrl || '',
      certFile: provider.certFile || '',
      keyFile: provider.keyFile || '',
      caBundleFile: provider.caBundleFile || '',
      model: provider.model,
      settings: {
        maxTokens: provider.settings.maxTokens,
        temperature: provider.settings.temperature,
        topP: provider.settings.topP ?? 1,
        frequencyPenalty: provider.settings.frequencyPenalty ?? 0,
        presencePenalty: provider.settings.presencePenalty ?? 0,
      },
    });
    setEditingId(provider.id);
    setIsAdding(true);

    // Fetch actual backend config to fill in non-secret values (endpoint, model, etc.)
    try {
      const { config } = await apiClient.getProviderConfig(provider.type);
      const secrets: Record<string, boolean> = {};

      // Map backend field names to form field names
      const fieldMap: Record<string, string> = {
        base_url: 'endpoint',
        auth_url: 'authUrl',
        cert_file: 'certFile',
        key_file: 'keyFile',
        ca_bundle_file: 'caBundleFile',
        model: 'model',
        default_model: 'model',
        // credentials / api_key are secrets — handled below
      };

      const updates: Partial<typeof formData> = {};

      for (const [key, value] of Object.entries(config)) {
        if (typeof value === 'object' && value !== null && 'is_set' in value) {
          // Secret field — track whether it's already configured
          secrets[key] = value.is_set;
        } else if (typeof value === 'string' && value) {
          const formKey = fieldMap[key];
          if (formKey) {
            (updates as any)[formKey] = value;
          }
        }
      }

      setSecretsSet(secrets);
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    } catch {
      // If config fetch fails, keep local values — form is still usable
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setSecretsSet({});
    resetForm();
  };

  const getProviderDefaults = (type: LLMProvider['type']) => {
    switch (type) {
      case 'gigachat':
        return {
          endpoint: 'https://gigachat.devices.sberbank.ru/api/v1',
          model: 'GigaChat-Max',
        };
      case 'ollama':
        return {
          endpoint: 'http://localhost:11434',
          model: 'llama3.1',
        };
      case 'openai':
        return {
          endpoint: 'https://api.openai.com/v1',
          model: 'gpt-4o',
        };
      case 'anthropic':
        return {
          endpoint: 'https://api.anthropic.com',
          model: 'claude-sonnet-4-20250514',
        };
      default:
        return {
          endpoint: '',
          model: '',
        };
    }
  };

  const handleTypeChange = (type: LLMProvider['type']) => {
    const defaults = getProviderDefaults(type);
    setFormData({
      ...formData,
      type,
      endpoint: defaults.endpoint,
      authUrl: '',
      certFile: '',
      keyFile: '',
      caBundleFile: '',
      model: defaults.model,
    });
  };

  const handleActivateProvider = async (provider: LLMProvider) => {
    // Clear previous errors/success for this provider
    setProviderErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(provider.id);
      return newMap;
    });
    setProviderSuccess(prev => {
      const newSet = new Set(prev);
      newSet.delete(provider.id);
      return newSet;
    });

    setInitializingProviders(prev => new Set(prev).add(provider.id));

    try {
      if (provider.enabled) {
        // Turn OFF — pure runtime toggle, credentials stay in Vault/.env and
        // the registration stays live in memory (just unusable for chat).
        // Deleting data is now the trash icon's job, not this button's.
        const result = await apiClient.disableLLMProvider(provider.type, provider.model);
        if (result.status !== 'success') {
          throw new Error(result.message || t.settings.providerDeactivationFailed || 'Deactivation failed');
        }
        setActiveLLMProvider(provider.id);
        triggerVaultRefresh();
        onProvidersChanged?.();
        toast.success(`${provider.name} deactivated`);
      } else {
        // Turn ON. The provider should already be registered on the backend
        // (Add now registers it disabled) — just flip the flag.
        const result = await apiClient.enableLLMProvider(provider.type, provider.model);

        if (result.status === 'not_found') {
          // Never registered on the backend — e.g. local state predating
          // this feature, or a prior Add that never reached the server.
          // Fall back to a full test+add, registered enabled right away.
          const providerConfig = buildProviderConfig(provider, true);
          const testResult = await apiClient.testLLMProvider(providerConfig);
          if (!providerTestPassed(testResult)) {
            throw new Error(testResult.message || t.settings.providerTestFailed);
          }
          const addResult = await apiClient.addLLMProvider(providerConfig);
          if (addResult.status !== 'success') {
            throw new Error(addResult.message || t.settings.providerInitializationFailed);
          }
          if (addResult.saved_to_storage === false) {
            toast.warning(t.settings.providerNotPersisted);
          }
        } else if (result.status !== 'success') {
          throw new Error(result.message || t.settings.providerInitializationFailed);
        }

        setActiveLLMProvider(provider.id);
        triggerVaultRefresh();
        onProvidersChanged?.();
        toast.success(`${provider.name} activated`);
      }

      setProviderSuccess(prev => new Set(prev).add(provider.id));
      setTimeout(() => {
        setProviderSuccess(prev => {
          const newSet = new Set(prev);
          newSet.delete(provider.id);
          return newSet;
        });
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setProviderErrors(prev => new Map(prev).set(provider.id, errorMessage));
      toast.error(errorMessage);

      // Clear error after 5 seconds
      setTimeout(() => {
        setProviderErrors(prev => {
          const newMap = new Map(prev);
          newMap.delete(provider.id);
          return newMap;
        });
      }, 5000);
    } finally {
      setInitializingProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider.id);
        return newSet;
      });
    }
  };

  const handleDeleteProvider = async (provider: LLMProvider) => {
    setInitializingProviders(prev => new Set(prev).add(provider.id));
    try {
      // Full removal — same endpoint/behavior "Deactivate" used before this
      // change: wipes the model from storage, and clears credentials too if
      // it was the last model of this provider type.
      const result = await apiClient.deactivateLLMProvider(provider.type, provider.model);
      if (result.status !== 'success') {
        throw new Error(result.message || 'Failed to delete provider');
      }
      deleteLLMProvider(provider.id);
      triggerVaultRefresh();
      onProvidersChanged?.();
      toast.success(`${provider.name} removed`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(errorMessage);
    } finally {
      setInitializingProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider.id);
        return newSet;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t.settings.llmProviders}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.settings.configureLanguageModels}
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t.settings.addProvider}
          </button>
        )}
      </div>

      {/* LLM Mode Selection */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          {t.settings.llmMode || 'LLM Mode'}
        </h4>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="llmMode"
              value="auto"
              checked={settings.llmMode === 'auto'}
              onChange={() => updateSettings({ llmMode: 'auto' as const })}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.settings.llmModeAuto || 'Auto (Parallel)'}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t.settings.llmModeAutoDescription || 'Run all available providers in parallel and merge results'}
              </p>
            </div>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="llmMode"
              value="single"
              checked={settings.llmMode === 'single'}
              onChange={() => {
                // Auto-select first enabled (or first available) provider when switching to single mode
                const autoSelect = settings.selectedLLMProvider
                  ? settings.selectedLLMProvider
                  : (llmProviders.find(p => p.enabled)?.id || llmProviders[0]?.id || undefined);
                updateSettings({ llmMode: 'single' as const, selectedLLMProvider: autoSelect });
              }}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.settings.llmModeSingle || 'Single Provider'}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t.settings.llmModeSingleDescription || 'Use only the selected provider'}
              </p>
            </div>
          </label>
        </div>

        {/* Provider Selection for Single Mode */}
        {settings.llmMode === 'single' && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.settings.selectProvider || 'Select Provider'}
            </label>
            <select
              value={settings.selectedLLMProvider || ''}
              onChange={(e) => updateSettings({ selectedLLMProvider: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t.settings.selectProvider || 'Select a provider...'}</option>
              {llmProviders.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} ({provider.type}) - {provider.model}
                </option>
              ))}
            </select>
            {settings.selectedLLMProvider && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t.settings.selectedProviderInfo || 'Selected provider will be used for all chat requests'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {isAdding && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            {editingId ? t.settings.editProvider : t.settings.addNewProvider}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.settings.providerName}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={t.settings.providerPlaceholder}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.settings.providerType}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value as LLMProvider['type'])}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>{t.settings.selectProviderType || 'Select provider type...'}</option>
                  <option value="gigachat">{t.settings.providerTypeGigachat}</option>
                  <option value="openai">{t.settings.providerTypeOpenai}</option>
                  <option value="anthropic">{t.settings.providerTypeAnthropic}</option>
                  <option value="ollama">{t.settings.providerTypeOllama}</option>
                  <option value="custom">{t.settings.providerTypeCustom}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.settings.apiKey}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    editingId && (secretsSet.credentials || secretsSet.api_key)
                      ? '••••••••  (already set — leave empty to keep)'
                      : t.settings.apiKeyPlaceholder
                  }
                  required={!editingId && formData.type !== 'ollama' && formData.type !== 'gigachat'}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {editingId && (secretsSet.credentials || secretsSet.api_key) && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t.settings.secretAlreadySet || 'Key is already configured. Only enter a value to change it.'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {formData.type === 'gigachat' ? (t.settings.gigachatBaseUrl || 'API Base URL') : t.settings.endpointUrl}
                </label>
                <input
                  type="url"
                  value={formData.endpoint}
                  onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={formData.type === 'gigachat' ? 'https://gigachat.devices.sberbank.ru/api/v1' : t.settings.endpointPlaceholder}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.settings.providerModel}
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={t.settings.modelPlaceholder}
                  required
                />
              </div>
            </div>

            {/* Provider-specific advanced configuration */}
            {formData.type === 'gigachat' && (
              <div className="space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t.settings.gigachatAdvanced || 'GigaChat Advanced'}
                </h5>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.settings.gigachatAuthUrl || 'OAuth URL'}
                  </label>
                  <input
                    type="url"
                    value={formData.authUrl}
                    onChange={(e) => setFormData({ ...formData, authUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="https://ngw.devices.sberbank.ru:9443/api/v2/oauth"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t.settings.gigachatCertFile || 'Client Certificate (PEM path)'}
                    </label>
                    <input
                      type="text"
                      value={formData.certFile}
                      onChange={(e) => setFormData({ ...formData, certFile: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      placeholder="/path/to/client-cert.pem"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t.settings.gigachatKeyFile || 'Client Key (PEM path)'}
                    </label>
                    <input
                      type="text"
                      value={formData.keyFile}
                      onChange={(e) => setFormData({ ...formData, keyFile: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      placeholder="/path/to/client-key.pem"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.settings.gigachatCaBundle || 'CA Bundle (PEM path)'}
                  </label>
                  <input
                    type="text"
                    value={formData.caBundleFile}
                    onChange={(e) => setFormData({ ...formData, caBundleFile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="/path/to/ca-bundle.pem"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t.settings.gigachatCertHint || 'For mTLS auth. Leave empty to use credentials-based auth. Inline PEM can be stored in Vault.'}
                  </p>
                </div>
              </div>
            )}

            {formData.type === 'openai' && (
              <div className="space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t.settings.providerOpenaiAdvanced || 'OpenAI Advanced'}
                </h5>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t.settings.providerOpenaiAdvancedHint || 'The endpoint URL above can be changed to use OpenAI-compatible APIs (e.g., Azure OpenAI, local proxies).'}
                </p>
              </div>
            )}

            {formData.type === 'anthropic' && (
              <div className="space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t.settings.providerAnthropicAdvanced || 'Anthropic Advanced'}
                </h5>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t.settings.providerAnthropicAdvancedHint || 'The endpoint URL above can be changed if using an Anthropic-compatible proxy.'}
                </p>
              </div>
            )}

            {/* Advanced Settings */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t.settings.modelParameters}
              </h5>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.settings.maxTokens}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="128000"
                    value={formData.settings.maxTokens}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, maxTokens: parseInt(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.settings.temperature} ({formData.settings.temperature})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={formData.settings.temperature}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, temperature: parseFloat(e.target.value) }
                    })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{t.settings.temperaturePrecise || 'Precise'}</span>
                    <span>{t.settings.temperatureBalanced || 'Balanced'}</span>
                    <span>{t.settings.temperatureCreative || 'Creative'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isRegistering}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isRegistering && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{editingId ? t.common.update : t.settings.addProvider}</span>
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isRegistering}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.common.cancel}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Provider List */}
      <div className="space-y-3">
        {llmProviders.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{t.settings.noProvidersConfigured}</p>
            <p className="text-sm">{t.settings.addProviderToEnable}</p>
          </div>
        ) : (
          llmProviders.map((provider) => (
            <div
              key={provider.id}
              className={clsx(
                'bg-white dark:bg-gray-800 border rounded-lg p-4 transition-colors',
                provider.enabled 
                  ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                      {provider.name}
                    </h4>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {provider.type === 'gigachat' ? t.settings.providerTypeGigachat :
                       provider.type === 'openai' ? t.settings.providerTypeOpenai :
                       provider.type === 'anthropic' ? t.settings.providerTypeAnthropic :
                       provider.type === 'ollama' ? t.settings.providerTypeOllama :
                       provider.type === 'custom' ? t.settings.providerTypeCustom :
                       String(provider.type).toUpperCase()}
                    </span>
                    {provider.enabled && (
                      <span className="flex items-center text-sm text-green-600 dark:text-green-400">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {t.settings.active}
                      </span>
                    )}
                    {/* STORY-520: real calls to this provider are failing */}
                    {isProviderFailing(provider.health) && (
                      <span
                        data-testid="provider-failing-badge"
                        title={failingDetails(provider, t)}
                        className="flex items-center text-sm text-amber-700 dark:text-amber-300"
                      >
                        <AlertCircle className="h-4 w-4 mr-1" aria-hidden="true" />
                        {t.settings.providerFailing}
                        <span className="sr-only">: {failingDetails(provider, t)}</span>
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {provider.model} • {t.settings.maxTokens}: {provider.settings.maxTokens}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleActivateProvider(provider)}
                    disabled={initializingProviders.has(provider.id)}
                    className={clsx(
                      'px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center space-x-1',
                      initializingProviders.has(provider.id)
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                        : provider.enabled
                        ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800'
                    )}
                  >
                    {initializingProviders.has(provider.id) ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>{t.settings.initializingProvider}</span>
                      </>
                    ) : provider.enabled ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        <span>{t.settings.active}</span>
                      </>
                    ) : (
                      <span>{t.settings.activate}</span>
                    )}
                  </button>
                  
                  {/* Error message */}
                  {providerErrors.has(provider.id) && (
                    <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 text-xs">
                      <AlertCircle className="h-3 w-3" />
                      <span>{providerErrors.get(provider.id)}</span>
                    </div>
                  )}
                  
                  {/* Success message */}
                  {providerSuccess.has(provider.id) && (
                    <div className="flex items-center space-x-1 text-green-600 dark:text-green-400 text-xs">
                      <CheckCircle className="h-3 w-3" />
                      <span>{t.settings.providerInitialized}</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleEdit(provider)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title={t.settings.editProvider}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteProvider(provider)}
                    disabled={initializingProviders.has(provider.id)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t.settings.deleteProvider}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};